<script setup>
// Tabela de templates da Meta (extraida do TemplatesPage na v73 pra ser
// reusada no menu Meta -> Template): linhas clicaveis expandem os detalhes
// (cabecalho/corpo/rodape/botoes e motivo de rejeicao).
import { ref } from 'vue';

defineProps({
  templates: {
    type: Array,
    required: true,
  },
});

const expandedId = ref(null);

const STATUS_LABELS = {
  APPROVED: 'Aprovado',
  PENDING: 'Em análise',
  IN_APPEAL: 'Em recurso',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
  PENDING_DELETION: 'Sendo excluído',
};

const STATUS_CLASSES = {
  APPROVED: 'bg-n-teal-3 text-n-teal-12',
  PENDING: 'bg-n-amber-3 text-n-amber-12',
  IN_APPEAL: 'bg-n-amber-3 text-n-amber-12',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-12',
  PAUSED: 'bg-n-ruby-3 text-n-ruby-12',
  DISABLED: 'bg-n-ruby-3 text-n-ruby-12',
};

const CATEGORY_LABELS = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação',
};

const statusLabel = status => STATUS_LABELS[status] || status || '—';
const statusClass = status =>
  STATUS_CLASSES[status] || 'bg-n-slate-3 text-n-slate-12';
const categoryLabel = category => CATEGORY_LABELS[category] || category || '—';

const qualityLabel = template => {
  const score = template.quality_score?.score;
  if (!score || score === 'UNKNOWN') return '—';
  return score;
};

const bodyPreview = template => {
  const body = (template.components || []).find(c => c.type === 'BODY');
  return body?.text || '';
};

const toggleExpand = template => {
  expandedId.value = expandedId.value === template.id ? null : template.id;
};

const buttonLabel = button => {
  if (button.type === 'URL') return `${button.text} → ${button.url}`;
  if (button.type === 'PHONE_NUMBER') return `${button.text} → ${button.phone_number}`;
  if (button.type === 'OTP') return button.text || 'Código OTP';
  if (button.type === 'COPY_CODE') return 'Copiar código';
  return button.text;
};
</script>

<template>
  <div class="overflow-x-auto rounded-lg border border-n-weak">
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak text-left text-n-slate-11">
          <th class="px-4 py-3 font-medium">Nome</th>
          <th class="px-4 py-3 font-medium">Categoria</th>
          <th class="px-4 py-3 font-medium">Idioma</th>
          <th class="px-4 py-3 font-medium">Status</th>
          <th class="px-4 py-3 font-medium">Qualidade</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="template in templates" :key="template.id">
          <tr
            class="border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-black1"
            @click="toggleExpand(template)"
          >
            <td class="px-4 py-3 font-mono text-n-slate-12">
              {{ template.name }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ categoryLabel(template.category) }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">{{ template.language }}</td>
            <td class="px-4 py-3">
              <span
                class="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                :class="statusClass(template.status)"
              >
                {{ statusLabel(template.status) }}
              </span>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ qualityLabel(template) }}
            </td>
          </tr>
          <tr v-if="expandedId === template.id" class="border-b border-n-weak">
            <td colspan="5" class="px-4 py-3 bg-n-alpha-black1">
              <div class="flex flex-col gap-2 max-w-3xl">
                <p
                  v-if="template.rejected_reason && template.rejected_reason !== 'NONE'"
                  class="text-xs text-n-ruby-11"
                >
                  Motivo da rejeição: {{ template.rejected_reason }}
                </p>
                <div
                  v-for="(component, idx) in template.components || []"
                  :key="idx"
                  class="text-xs text-n-slate-11"
                >
                  <template v-if="component.type === 'HEADER'">
                    <span class="font-medium text-n-slate-12">Cabeçalho:</span>
                    {{ component.format === 'TEXT' ? component.text : component.format }}
                  </template>
                  <template v-else-if="component.type === 'BODY'">
                    <span class="font-medium text-n-slate-12">Corpo:</span>
                    <span class="whitespace-pre-wrap">{{ component.text || '(definido pela Meta)' }}</span>
                  </template>
                  <template v-else-if="component.type === 'FOOTER'">
                    <span class="font-medium text-n-slate-12">Rodapé:</span>
                    {{ component.text }}
                  </template>
                  <template v-else-if="component.type === 'BUTTONS'">
                    <span class="font-medium text-n-slate-12">Botões:</span>
                    {{ (component.buttons || []).map(buttonLabel).join(' | ') }}
                  </template>
                </div>
                <p v-if="!bodyPreview(template) && template.category === 'AUTHENTICATION'" class="text-xs text-n-slate-10">
                  Template de autenticação — conteúdo padrão definido pela Meta.
                </p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
